import {TranslateService} from '../../shared/translate.service';
import {AfterViewInit, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from '@angular/material';
import {AppConfigService} from '../../config/app-config.service';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {BaseTableDataSource} from './base-table-data-source';
import {AppUtils} from '../../shared/app-utils';
import {UserItemDeletePromptComponent} from './user-item-delete-prompt/user-item-delete-prompt.component';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {MessageService} from '../../messages/message.service';
import {AuthService} from '../auth.service';
import {BaseUserTableService} from './base-user-table.service';
import {UserQueryDto} from "./user-queries/dto/user-query-dto";

export abstract class BaseUserTableView<T, F> implements OnInit, OnDestroy, AfterViewInit {

  @ViewChild(MatSort) sorter: MatSort;

  filtersActive: boolean = false;
  isLoading: boolean = false;
  page: number = 1;
  totalPages: number = 0;
  hasNext: boolean = false;
  hasPrev: boolean = false;
  length: number = 0;
  pageSize: number = 0;
  pageSizeOptions: number[] = [];
  sort: string = 'id';
  direction: string = 'asc';
  dataSource: BaseTableDataSource<T, F>;

  protected initialized = false;
  protected readonly dateFormat = 'YYYY-MM-DD';

  protected constructor(protected translator: TranslateService, protected configService: AppConfigService,
                        protected router: Router, protected route: ActivatedRoute, protected modalService: NgbModal,
                        protected messageService: MessageService, protected authService: AuthService,
                        protected userService: BaseUserTableService<T, F>) {
  }

  public abstract getPrefix(): string;

  public abstract loadMore();

  launchDeletePrompt(element: any) {
    const modalInstance = this.modalService.open(UserItemDeletePromptComponent);
    modalInstance.componentInstance.windowTitle = this.getDeletePrefix();
    modalInstance.componentInstance.itemTitle = element.name;
    if (!modalInstance.componentInstance.itemTitle){
      modalInstance.componentInstance.itemTitle = element.title;
    }
    modalInstance.result.then(result => {
      if (result) {
        this.authService.getToken().then(token => {
          this.userService.delete(element.id, token).subscribe(result => {
            if (result) {
              this.messageService
                .successMessage(this.getDeletePrefix() + 'Result.title', this.getDeletePrefix() + 'Result.desc');
              this.navigateByParams();
            } else {
              this.messageService
                .errorMessage(this.getDeletePrefix() + 'Result.title.fail', this.getDeletePrefix() + 'Result.desc.fail');
            }
          }, error1 => {
            console.log(error1);
            this.messageService
              .errorMessage(this.getDeletePrefix() + 'Result.title.fail', this.getDeletePrefix() + 'Result.desc.fail');
          });
        });
      }
    });
  }

  ngOnInit() {
    const config = this.configService.getConfig();
    this.pageSize = config.defaultPageSize;
    this.pageSizeOptions = config.pageSizeOptions;
    this.route.queryParamMap.subscribe(params => {
      this.populateQueryParams(params);
      if (!this.initialized) {
        this.initFilters();
        this.initialized = true;
      }
    });
    this.dataSource.getLoadingState().subscribe(loading => {
      this.isLoading = loading;
    });
    this.sorter.sortChange.subscribe((sortChange: Sort) => {
      this.sort = sortChange.active;
      this.direction = sortChange.direction;
      this.page = 1;
      return this.navigateByParams();
    });
    this.dataSource.getPageState().subscribe(page => {
      this.length = page.totalElements;
      this.totalPages = page.totalPages;
      this.hasNext = !page.last;
      this.hasPrev = !page.first
    });
  }

  preventDef(event) {
    if (event) {
      event.preventDefault();
    }
  }

  getLabel(value: number) {
    if (value == 0 || value >= 5) {
      return this.translator.translate(this.getPrefix() + '.05');
    } else if (value == 1) {
      return this.translator.translate(this.getPrefix() + '.1');
    } else {
      return this.translator.translate(this.getPrefix() + '.all');
    }
  }

  ngAfterViewInit() {
    this.dataSource.loadCollections(1, this.pageSize);
  }

  ngOnDestroy() {
    this.sorter.sortChange.complete();
  }

  goToPrev() {
    this.page = this.page - 1;
    this.navigateByParams();
  }

  goToNext() {
    this.page = this.page + 1;
    this.navigateByParams();
  }

  onPageChange() {
    this.navigateByParams();
  }

  onPageSizeChange() {
    this.page = 1;
    this.navigateByParams();
  }

  hideFiltersLabel() {
    return this.translator.translate('filters.label');
  }

  hideSortLabel(){
    return this.translator.translate('sorts.label');
  }

  toggleFilters() {
    this.filtersActive = !this.filtersActive;
  }

  formatTime(date: number) {
    return AppUtils.formatDate(date);
  }

  containsNewElements(row: UserQueryDto): boolean {
    return row.newItemsCount > 0;
  }

  protected abstract getDeletePrefix(): string;

  protected abstract navigateByParams();

  protected abstract populateQueryParams(params: ParamMap);

  protected abstract initFilters();

}
